<script setup lang="ts">
import { computed, reactive, watch } from 'vue'
import {
  deleteAtPath,
  fieldIsRequired,
  fieldIsVisible,
  getAtPath,
  isMissingValue,
  setAtPath,
  validateData,
  type ClinicCrfData,
  type ClinicCrfField,
  type ClinicCrfMissingCode,
  type ClinicCrfPackageV1,
  type MissingValue,
  type PartialDateValue,
} from '@clinic-crf/contract'
import { FlaskConical, RotateCcw, ShieldCheck } from 'lucide-vue-next'
import { sanitizePreviewAnswers } from '@/lib/editor'

const props = defineProps<{ packageValue: ClinicCrfPackageV1; annotated?: boolean }>()
const answers = reactive<ClinicCrfData>({})

const validation = computed(() => validateData(props.packageValue.definition, answers))
const errorCount = computed(() => Object.values(validation.value.errors).flat().length)

watch(() => props.packageValue.form.code, reset)
watch(
  () => props.packageValue.definition,
  (definition) => {
    const sanitized = sanitizePreviewAnswers(definition, answers)
    for (const key of Object.keys(answers)) delete answers[key]
    Object.assign(answers, sanitized)
  },
  { deep: true },
)

function reset(): void {
  for (const key of Object.keys(answers)) delete answers[key]
}

function answer(field: ClinicCrfField): unknown {
  return getAtPath(answers, field.path)
}

function scalarAnswer(field: ClinicCrfField): string | number {
  const value = answer(field)
  return typeof value === 'string' || typeof value === 'number' ? value : ''
}

function setAnswer(field: ClinicCrfField, value: unknown): void {
  if (value === '' || value === undefined) deleteAtPath(answers, field.path)
  else setAtPath(answers, field.path, value)
}

function inputValue(event: Event, field: ClinicCrfField): void {
  const raw = (event.target as HTMLInputElement).value
  if (raw === '') return setAnswer(field, undefined)
  if (field.type === 'integer') setAnswer(field, Number.parseInt(raw, 10))
  else if (field.type === 'number') setAnswer(field, Number(raw))
  else if (field.type === 'datetime') setAnswer(field, new Date(raw).toISOString())
  else setAnswer(field, raw)
}

function multichoice(field: ClinicCrfField, option: string, enabled: boolean): void {
  const current = Array.isArray(answer(field)) ? [...answer(field) as string[]] : []
  setAnswer(field, enabled ? [...current, option] : current.filter((item) => item !== option))
}

function partialDate(field: ClinicCrfField, part: keyof PartialDateValue, event: Event): void {
  const current = (answer(field) ?? {}) as PartialDateValue
  const raw = (event.target as HTMLInputElement).value
  const next = { ...current }
  if (raw) next[part] = Number(raw)
  else delete next[part]
  setAnswer(field, Object.keys(next).length ? next : undefined)
}

function missing(field: ClinicCrfField, code: string): void {
  if (!code) return setAnswer(field, undefined)
  setAnswer(field, { __missing: { code: code as ClinicCrfMissingCode } })
}

function missingReason(field: ClinicCrfField, reason: string): void {
  const value = answer(field)
  if (!isMissingValue(value)) return
  setAnswer(field, { __missing: { ...value.__missing, reason } })
}

function missingValue(field: ClinicCrfField): MissingValue | null {
  const value = answer(field)
  return isMissingValue(value) ? value : null
}

function idFor(path: string): string {
  return `preview-${path.replace(/[^a-z0-9]/gi, '-')}`
}

function labelFor(field: ClinicCrfField, option: string): string {
  return props.packageValue.presentation?.fields?.[field.path]?.option_labels?.[option] ?? option
}

function fieldsForSection(section: { field_paths: string[] }): ClinicCrfField[] {
  const byPath = new Map(props.packageValue.definition.fields.map((field) => [field.path, field]))
  return section.field_paths.map((path) => byPath.get(path)).filter((field): field is ClinicCrfField => Boolean(field))
}

function localDateTime(value: unknown): string {
  if (typeof value !== 'string') return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000)
  return local.toISOString().slice(0, 16)
}
</script>

<template>
  <div class="preview-shell">
    <div class="preview-toolbar no-print">
      <div>
        <span class="live-dot"></span>
        <strong>Podgląd na żywo</strong>
        <span class="muted">· dane tylko w pamięci tej karty</span>
      </div>
      <button class="button button-quiet button-small" type="button" @click="reset"><RotateCcw :size="15" /> Wyczyść test</button>
    </div>

    <div class="paper">
      <header class="paper-header">
        <div class="paper-brand"><span>SB</span><div><strong>StrefaBadań</strong><small>eCRF Studio · Sandbox</small></div></div>
        <div class="paper-meta"><span>{{ packageValue.form.code }}</span><span>wersja {{ packageValue.form.version }}</span></div>
      </header>
      <div class="paper-title">
        <span class="eyebrow">Formularz testowy</span>
        <h1>{{ packageValue.form.name }}</h1>
        <p v-if="packageValue.form.description">{{ packageValue.form.description }}</p>
        <div class="sandbox-callout"><ShieldCheck :size="18" /><span><strong>Tryb projektowy.</strong> Używaj wyłącznie danych syntetycznych — to nie jest system do prowadzenia badania.</span></div>
      </div>

      <section v-for="(section, sectionIndex) in packageValue.presentation?.sections" :key="section.id" class="preview-section">
        <div class="preview-section-heading"><span>{{ String(sectionIndex + 1).padStart(2, '0') }}</span><div><h2>{{ section.title }}</h2><p v-if="section.description">{{ section.description }}</p></div></div>
        <div class="preview-grid">
          <template v-for="field in fieldsForSection(section)" :key="field.path">
            <div
              v-if="fieldIsVisible(field, answers)"
              :class="['preview-field', packageValue.presentation?.fields?.[field.path]?.width === 'half' ? 'half' : 'full']"
            >
                <div v-if="annotated" class="annotation"><code>{{ field.path }}</code><span>{{ field.type }}</span><span v-if="field.source">źródło: {{ field.source }}</span><span v-if="field.unit">jednostka: {{ field.unit }}</span><span v-if="field.min !== undefined || field.max !== undefined">zakres: {{ field.min ?? '–∞' }}…{{ field.max ?? '∞' }}</span></div>
                <label :for="idFor(field.path)" class="preview-label">{{ field.label }}<sup v-if="fieldIsRequired(field, answers)" aria-label="wymagane">*</sup><span v-if="field.unit" class="unit">{{ field.unit }}</span></label>
                <p v-if="packageValue.presentation?.fields?.[field.path]?.help_text" class="help-text">{{ packageValue.presentation?.fields?.[field.path]?.help_text }}</p>

                <input v-if="field.type === 'string'" :id="idFor(field.path)" type="text" :value="scalarAnswer(field)" :placeholder="packageValue.presentation?.fields?.[field.path]?.placeholder" @input="inputValue($event, field)" />
                <textarea v-else-if="field.type === 'text'" :id="idFor(field.path)" rows="4" :value="scalarAnswer(field)" :placeholder="packageValue.presentation?.fields?.[field.path]?.placeholder" @input="inputValue($event, field)"></textarea>
                <input v-else-if="field.type === 'integer' || field.type === 'number'" :id="idFor(field.path)" type="number" :step="field.type === 'integer' ? 1 : 10 ** -(field.precision ?? 2)" :min="field.min" :max="field.max" :value="scalarAnswer(field)" @input="inputValue($event, field)" />
                <input v-else-if="field.type === 'date'" :id="idFor(field.path)" type="date" :value="scalarAnswer(field)" @input="inputValue($event, field)" />
                <input v-else-if="field.type === 'datetime'" :id="idFor(field.path)" type="datetime-local" :value="localDateTime(answer(field))" @input="inputValue($event, field)" />

                <div v-else-if="field.type === 'boolean'" :id="idFor(field.path)" class="radio-grid" role="radiogroup" :aria-label="field.label">
                  <label><input type="radio" :name="idFor(field.path)" :checked="answer(field) === true" @change="setAnswer(field, true)" /><span>Tak</span></label>
                  <label><input type="radio" :name="idFor(field.path)" :checked="answer(field) === false" @change="setAnswer(field, false)" /><span>Nie</span></label>
                </div>

                <select v-else-if="field.type === 'choice'" :id="idFor(field.path)" :value="answer(field) ?? ''" @change="setAnswer(field, ($event.target as HTMLSelectElement).value || undefined)">
                  <option value="">Wybierz odpowiedź…</option><option v-for="option in field.options" :key="option" :value="option">{{ labelFor(field, option) }}</option>
                </select>
                <div v-else-if="field.type === 'multichoice'" :id="idFor(field.path)" class="checkbox-list" role="group" :aria-label="field.label">
                  <label v-for="option in field.options" :key="option"><input type="checkbox" :checked="Array.isArray(answer(field)) && (answer(field) as string[]).includes(option)" @change="multichoice(field, option, ($event.target as HTMLInputElement).checked)" /><span>{{ labelFor(field, option) }}</span></label>
                </div>
                <div v-else-if="field.type === 'partial_date'" :id="idFor(field.path)" class="partial-date" role="group" :aria-label="field.label">
                  <label><span>Rok</span><input type="number" min="1000" max="9999" :value="(answer(field) as PartialDateValue | undefined)?.year" @input="partialDate(field, 'year', $event)" /></label>
                  <label><span>Miesiąc</span><input type="number" min="1" max="12" :value="(answer(field) as PartialDateValue | undefined)?.month" @input="partialDate(field, 'month', $event)" /></label>
                  <label><span>Dzień</span><input type="number" min="1" max="31" :value="(answer(field) as PartialDateValue | undefined)?.day" @input="partialDate(field, 'day', $event)" /></label>
                </div>

                <div v-if="field.missing_codes?.length" class="missing-row">
                  <label><span>Jawny brak danych</span><select :value="missingValue(field)?.__missing.code ?? ''" @change="missing(field, ($event.target as HTMLSelectElement).value)"><option value="">Brak — podaj odpowiedź</option><option v-for="code in field.missing_codes" :key="code" :value="code">{{ code }}</option></select></label>
                  <label v-if="field.missing_reason_required && missingValue(field)"><span>Powód</span><input :value="missingValue(field)?.__missing.reason ?? ''" @input="missingReason(field, ($event.target as HTMLInputElement).value)" /></label>
                </div>
                <ul v-if="validation.errors[field.path]?.length" class="field-errors" aria-live="polite"><li v-for="error in validation.errors[field.path]" :key="error">{{ error }}</li></ul>
            </div>
          </template>
        </div>
      </section>

      <footer class="paper-footer">
        <div><FlaskConical :size="17" /><span>Test formularza · wartości nie są zapisywane</span></div>
        <span :class="errorCount ? 'status-error' : 'status-ok'">{{ errorCount ? `${errorCount} uwag testu` : 'Brak błędów dla bieżących danych' }}</span>
      </footer>
    </div>
  </div>
</template>
